<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-dialog v-model="dialog" max-width="600px" persistent scrollable>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Add Template </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6" style="max-height: 70vh; overflow-y: auto">
          <v-form ref="form" v-model="valid" class="px-2">
            <div class="mb-4">
              <v-text-field
                ref="templateNameInput"
                v-model="formData.template_name"
                :rules="templateNameRules"
                label="Template Name"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
              ></v-text-field>

              <v-autocomplete
                v-model="formData.category_id"
                :items="categories"
                item-title="category_name"
                item-value="category_id"
                :rules="[(v) => !!v || 'Category is required']"
                label="Category"
                variant="outlined"
                density="compact"
                class="mb-2"
                clearable
                required
              ></v-autocomplete>

              <v-autocomplete
                v-model="formData.routing_type"
                :items="routingTypeOptions"
                :rules="[(v) => !!v || 'Routing is required']"
                label="Routing"
                variant="outlined"
                density="compact"
                class="mb-2"
                clearable
                required
              ></v-autocomplete>

              <div class="d-flex align-center justify-space-between mb-2">
                <label class="v-label text-body-2">Template Description</label>
              </div>

              <div class="rich-text-editor">
                <v-card variant="outlined" class="editor-container">
                  <div class="editor-toolbar">
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :class="{ active: activeFormats.bold }"
                      :color="activeFormats.bold ? 'primary' : undefined"
                      @click="formatText('bold')"
                      title="Bold"
                    >
                      <v-icon>mdi-format-bold</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :class="{ active: activeFormats.italic }"
                      :color="activeFormats.italic ? 'primary' : undefined"
                      @click="formatText('italic')"
                      title="Italic"
                    >
                      <v-icon>mdi-format-italic</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :class="{ active: activeFormats.underline }"
                      :color="activeFormats.underline ? 'primary' : undefined"
                      @click="formatText('underline')"
                      title="Underline"
                    >
                      <v-icon>mdi-format-underline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      @mousedown.prevent
                      @click="openPlaceholderDialog"
                      title="Add Placeholder"
                    >
                      <v-icon>mdi-code-braces</v-icon>
                    </v-btn>
                  </div>
                  <v-divider></v-divider>
                  <div
                    ref="editorContent"
                    contenteditable="true"
                    class="editor-content"
                    @input="handleEditorInput"
                    @focus="updateToolbarState"
                    @keyup="updateToolbarState"
                    @mouseup="updateToolbarState"
                    @click="updateToolbarState"
                  ></div>
                </v-card>
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="handleClose" :disabled="loading">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!valid || loading"
            :loading="loading"
            @click="handleSubmit"
          >
            Add Template
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="placeholderDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4"> Add Placeholder </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <v-text-field
            ref="placeholderInput"
            v-model="placeholderLabel"
            :rules="placeholderLabelRules"
            label="Label Name"
            variant="outlined"
            density="compact"
            @keyup.enter="insertPlaceholder"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="placeholderDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!cleanPlaceholderLabel"
            @click="insertPlaceholder"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { create_template } from "@/graphql/mutations";
import GetCategories from "@/mixins/Category/GetCategories";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

export default {
  name: "AddTemplateDialog",
  mixins: [GetCategories],
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "BMS",
    },
  },
  emits: ["update:modelValue", "added"],
  data() {
    return {
      valid: false,
      loading: false,
      categories: [],
      routingTypeOptions: [
        "IDT",
        "Internal",
        "Nursing",
        "Physician",
        "Psych",
        "None",
      ],
      formData: {
        template_name: "",
        template_description: "",
        category_id: null,
        routing_type: null,
      },
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      templateNameRules: [(v) => !!v || "Template name is required"],
      isUpdatingEditor: false,
      activeFormats: {
        bold: false,
        italic: false,
        underline: false,
      },
      placeholderDialog: false,
      placeholderLabel: "",
      placeholderRange: null,
      placeholderLabelRules: [
        (v) => !!(v || "").replace(/[{}]/g, "").trim() || "Label name is required",
      ],
    };
  },
  computed: {
    cleanPlaceholderLabel() {
      return (this.placeholderLabel || "").replace(/[{}]/g, "").trim();
    },
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.loadCategories();
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.templateNameInput?.focus();
          }, 100);
        });
        this.$nextTick(() => {
          if (this.$refs.editorContent) {
            this.$refs.editorContent.innerHTML =
              this.formData.template_description || "";
          }
        });
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    async loadCategories() {
      try {
        await this.fetchCategories();
        this.categories = (this.categoriesList || []).map((item) => ({
          category_id: item.category_id || item.id,
          category_name: item.category_name,
        }));
      } catch (error) {
        console.error("Error loading categories:", error);
        this.categories = [];
      }
    },
    getSelectedCategoryName() {
      const selectedCategory = this.categories.find(
        (category) =>
          String(category.category_id) === String(this.formData.category_id)
      );
      return selectedCategory?.category_name || "";
    },
    formatText(command) {
      if (this.$refs.editorContent) {
        this.$refs.editorContent.focus();
      }

      document.execCommand(command, false, null);

      this.$nextTick(() => {
        this.updateToolbarState();
        this.handleEditorInput();
      });
    },
    updateToolbarState() {
      this.$nextTick(() => {
        if (!this.$refs.editorContent) return;
        const hasFocus = document.activeElement === this.$refs.editorContent;
        if (!hasFocus) return;

        try {
          this.activeFormats.bold = document.queryCommandState("bold");
          this.activeFormats.italic = document.queryCommandState("italic");
          this.activeFormats.underline = document.queryCommandState("underline");
        } catch (e) {
          // ignore query errors from unsupported states
        }
      });
    },
    openPlaceholderDialog() {
      const selection = window.getSelection();
      const editor = this.$refs.editorContent;
      this.placeholderRange =
        selection.rangeCount &&
        editor.contains(selection.getRangeAt(0).commonAncestorContainer)
          ? selection.getRangeAt(0).cloneRange()
          : null;
      this.placeholderLabel = "";
      this.placeholderDialog = true;
      this.$nextTick(() =>
        setTimeout(() => this.$refs.placeholderInput?.focus(), 100)
      );
    },
    insertPlaceholder() {
      const label = this.cleanPlaceholderLabel;
      if (!label) return;
      const editor = this.$refs.editorContent;
      editor.focus();
      let range = this.placeholderRange;
      if (!range) {
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
      }
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand("insertText", false, `{{${label}}}`);
      this.placeholderDialog = false;
      this.handleEditorInput();
    },
    handleEditorInput() {
      if (this.$refs.editorContent && !this.isUpdatingEditor) {
        const content = this.$refs.editorContent.innerHTML;
        this.isUpdatingEditor = true;
        this.formData.template_description = content;
        this.$nextTick(() => {
          this.isUpdatingEditor = false;
        });
      }
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        template_name: "",
        template_description: "",
        category_id: null,
        routing_type: null,
      };
      if (this.$refs.editorContent) {
        this.$refs.editorContent.innerHTML = "";
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.loading = true;

      try {
        const input = {
          template_name: this.formData.template_name,
          template_description: this.formData.template_description || "",
          category_id: this.formData.category_id || "",
          category_name: this.getSelectedCategoryName(),
          routing_type: this.formData.routing_type || "",
          source: this.source,
        };

        const response = await client.graphql({
          query: create_template,
          variables: {
            input: input,
          },
        });

        // console.log(response);
        const responseData = JSON.parse(response.data.create_template);

        if (responseData.status == "SUCCESS") {
          this.loading = false;
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Template added successfully",
            color: "success",
          };
          this.$emit("added", 0);
          this.handleClose();
        } else {
          this.loading = false;
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Failed to add template",
            color: "error",
          };
        }
      } catch (error) {
        this.loading = false;
        console.error("Error adding template:", error);
        this.snackbar = {
          show: true,
          message:
            error.message || "An error occurred while adding the template",
          color: "error",
        };
      }
    },
  },
};
</script>

<style scoped>
.rich-text-editor {
  width: 100%;
}

.editor-container {
  border-radius: 4px;
}

.editor-toolbar {
  display: flex;
  gap: 4px;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.editor-toolbar .v-btn {
  min-width: 32px;
  width: 32px;
  height: 32px;
}

.editor-toolbar .v-btn.active,
.editor-toolbar .v-btn[color="primary"] {
  background-color: rgba(var(--v-theme-primary), 0.2) !important;
  color: rgb(var(--v-theme-primary)) !important;
  border: 1px solid rgba(var(--v-theme-primary), 0.3) !important;
}

.editor-toolbar .v-btn.active .v-icon,
.editor-toolbar .v-btn[color="primary"] .v-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

.editor-content {
  min-height: 120px;
  max-height: 240px;
  overflow-y: auto;
  padding: 12px;
  outline: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
}

.editor-content:focus {
  outline: none;
}
</style>

